import { useState } from 'react';
import apiKeysService, { ApiKeyUsageSummary } from '@services/api-keys.service';
import {
  SectionMessage,
  StatusBadge,
  rowClass,
  sectionHeadingClass,
  tableClass,
  tableWrapClass,
  tdClass,
  thClass,
  theadClass,
} from './apiKeyInsightsUi';

/** Endpoints shown before "Show all" — a key normally calls a handful. */
const ENDPOINT_PREVIEW_ROWS = 8;
/** Same threshold the ERROR RATE tile uses to turn red. */
const HIGH_ERROR_RATE_PERCENT = 10;

const numericThClass = `${thClass} text-right`;
const numericTdClass = `${tdClass} text-right tabular-nums`;
const errorTextClass = 'font-semibold text-red-600 dark:text-red-400';

/**
 * "By endpoint" and "Errors by type" for one API key, over the same window as
 * the tiles and the chart above them. Answers "which calls fail, and why?"
 * before the admin has to read individual requests.
 */
export default function ApiKeyUsageBreakdown({
  summary,
  days = 7,
}: {
  summary: ApiKeyUsageSummary;
  days?: number;
}) {
  const [showAllEndpoints, setShowAllEndpoints] = useState(false);

  const windowLabel = `last ${days} ${days === 1 ? 'day' : 'days'}`;
  const totalErrors = summary.daily.reduce((sum, day) => sum + day.errors, 0);
  const endpoints = summary.byEndpoint;
  const visibleEndpoints = showAllEndpoints
    ? endpoints
    : endpoints.slice(0, ENDPOINT_PREVIEW_ROWS);

  return (
    <>
      <section aria-label="Usage by endpoint">
        <h4 className={`${sectionHeadingClass} mb-1.5`}>By endpoint ({windowLabel})</h4>
        {summary.unavailable ? (
          <SectionMessage>The endpoint breakdown is unavailable right now.</SectionMessage>
        ) : endpoints.length === 0 ? (
          <SectionMessage>No requests in the {windowLabel}.</SectionMessage>
        ) : (
          <>
            <div className={tableWrapClass}>
              <table className={tableClass}>
                <thead className={theadClass}>
                  <tr>
                    <th scope="col" className={thClass}>Endpoint</th>
                    <th scope="col" className={numericThClass}>Requests</th>
                    <th scope="col" className={numericThClass}>Errors</th>
                    <th scope="col" className={numericThClass}>Error rate</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEndpoints.map((row) => {
                    const errorRate = apiKeysService.getErrorRate(row.requests, row.errors);
                    return (
                      <tr key={row.endpoint} className={rowClass}>
                        <td className={`${tdClass} font-mono break-all`}>{row.endpoint}</td>
                        <td className={numericTdClass}>{row.requests.toLocaleString()}</td>
                        <td className={`${numericTdClass} ${row.errors > 0 ? errorTextClass : ''}`}>
                          {row.errors.toLocaleString()}
                        </td>
                        <td
                          className={`${numericTdClass} ${
                            errorRate > HIGH_ERROR_RATE_PERCENT ? errorTextClass : ''
                          }`}
                        >
                          {errorRate.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {endpoints.length > ENDPOINT_PREVIEW_ROWS && (
              <button
                type="button"
                onClick={() => setShowAllEndpoints((value) => !value)}
                className="mt-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                {showAllEndpoints
                  ? 'Show fewer'
                  : `Show all ${endpoints.length} endpoints`}
              </button>
            )}
          </>
        )}
      </section>

      <section aria-label="Errors by type">
        <h4 className={`${sectionHeadingClass} mb-1.5`}>Errors by type ({windowLabel})</h4>
        {summary.unavailable ? (
          <SectionMessage>The error breakdown is unavailable right now.</SectionMessage>
        ) : summary.byError.length === 0 ? (
          <SectionMessage>
            {totalErrors > 0
              ? // The tiles count every request; error details exist only for
                // requests made since request logging was switched on.
                `${totalErrors.toLocaleString()} ${
                  totalErrors === 1 ? 'error was' : 'errors were'
                } counted in the ${windowLabel}, but no details were recorded for ${
                  totalErrors === 1 ? 'it' : 'them'
                }. Details are kept for requests made after request logging was enabled.`
              : `No errors in the ${windowLabel}.`}
          </SectionMessage>
        ) : (
          <div className={tableWrapClass}>
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={thClass}>Error code</th>
                  <th scope="col" className={numericThClass}>Count</th>
                  <th scope="col" className={thClass}>Last seen</th>
                  <th scope="col" className={thClass}>Sample message</th>
                </tr>
              </thead>
              <tbody>
                {summary.byError.map((row) => (
                  <tr key={`${row.statusCode}:${row.errorCode ?? ''}`} className={rowClass}>
                    <td className={tdClass}>
                      <StatusBadge statusCode={row.statusCode} />
                    </td>
                    <td className={`${tdClass} font-mono break-all`}>
                      {row.errorCode ?? <span className="text-gray-400 dark:text-gray-500">—</span>}
                    </td>
                    <td className={`${numericTdClass} ${errorTextClass}`}>
                      {row.count.toLocaleString()}
                    </td>
                    <td
                      className={`${tdClass} whitespace-nowrap`}
                      title={apiKeysService.formatExactTime(row.lastOccurredAt)}
                    >
                      {apiKeysService.formatRelativeTime(row.lastOccurredAt)}
                    </td>
                    <td className={tdClass}>
                      {row.sampleMessage ? (
                        <span className="block max-w-md truncate" title={row.sampleMessage}>
                          {row.sampleMessage}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
